import React, { useState, useEffect } from 'react';
import { Clock, Calendar, AlertCircle, ChevronUp, ChevronDown } from 'lucide-react';

export interface DeadlineItem {
  id: string;
  title: string;
  dueDate: string;
  urgency: 'urgent' | 'soon' | 'upcoming';
  category?: string;
}

export interface DeadlineTickerProps {
  items: DeadlineItem[];
  onItemClick?: (item: DeadlineItem) => void;
}

export const DeadlineTicker: React.FC<DeadlineTickerProps> = ({ items, onItemClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Always show the ticker, even with no items
  const hasItems = items.length > 0;
  
  // Debug logging
  useEffect(() => {
    console.log('[DeadlineTicker] Items updated:', items);
  }, [items]);

  // Auto-cycle through items when collapsed (ticker effect)
  useEffect(() => {
    if (!isExpanded && hasItems && items.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % items.length);
      }, 5000); // Change every 5 seconds
      
      return () => clearInterval(interval);
    }
  }, [isExpanded, hasItems, items.length]);

  const getUrgencyIcon = (urgency: string) => {
    switch (urgency) {
      case 'urgent':
        return <AlertCircle className="h-3 w-3 text-red-400" />;
      case 'soon':
        return <Clock className="h-3 w-3 text-amber-400" />;
      default:
        return <Calendar className="h-3 w-3 text-blue-400" />;
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'urgent':
        return 'text-red-300 border-red-500/30 bg-red-900/20 hover:bg-red-900/30';
      case 'soon':
        return 'text-amber-300 border-amber-500/30 bg-amber-900/20 hover:bg-amber-900/30';
      default:
        return 'text-blue-300 border-blue-500/30 bg-blue-900/20 hover:bg-blue-900/30';
    }
  };

  return (
    <div className="relative">
      {/* Toggle Button / Current Item Display - BIGGER & MORE VISIBLE */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-purple-900/40 to-indigo-900/40 backdrop-blur-md border-2 border-purple-400/30 hover:bg-purple-900/50 transition-all shadow-lg"
      >
        {hasItems && !isExpanded ? (
          // Show current cycling item when collapsed - LARGER TEXT
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {getUrgencyIcon(items[currentIndex].urgency)}
            <span className="text-base font-bold text-purple-200 truncate">
              {items[currentIndex].title}
            </span>
            <span className="text-sm text-purple-300 whitespace-nowrap font-semibold">
              {items[currentIndex].dueDate}
            </span>
          </div>
        ) : (
          // Show count when expanded or no items
          <span className="text-xs font-semibold text-purple-300 uppercase tracking-wide flex items-center gap-2">
            <Calendar className="h-3 w-3" />
            Upcoming Deadlines ({items.length})
          </span>
        )}
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-purple-300 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-purple-300 flex-shrink-0" />
        )}
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="bg-black/20 backdrop-blur-md border-b border-purple-500/20 max-h-40 overflow-y-auto">
          {/* Purple glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 via-indigo-500/5 to-purple-500/5 pointer-events-none" />
          
          <div className="relative py-2 px-4 space-y-2">
            {hasItems ? (
              items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onItemClick?.(item)}
                  className={`
                    w-full flex items-center gap-2 px-3 py-1.5 rounded-lg
                    border backdrop-blur-sm
                    transition-all duration-200
                    hover:scale-[1.02] hover:brightness-125
                    text-left
                    ${getUrgencyColor(item.urgency)}
                  `}
                >
                  {getUrgencyIcon(item.urgency)}
                  <span className="text-xs font-semibold flex-1">
                    {item.title}
                  </span>
                  <span className="text-[10px] opacity-70 whitespace-nowrap">
                    {item.dueDate}
                  </span>
                </button>
              ))
            ) : (
              <div className="text-center py-4 px-3">
                <p className="text-xs text-slate-400">No upcoming deadlines</p>
                <p className="text-[10px] text-slate-500 mt-1">Add assignments with due dates in your organizer</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
